import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, AlertTriangle, CheckCircle, Activity, Heart, X } from 'lucide-react';
import { Contact } from '../types';

interface PulseFeedbackProps {
    senderName: string;
    senderAvatar: string;
    status: string; // 'SAFE', 'DANGER', 'SOS', 'OK'
    onDismiss: () => void;
}

const PulseFeedback: React.FC<PulseFeedbackProps> = ({ senderName, senderAvatar, status, onDismiss }) => {

    useEffect(() => {
        const timer = setTimeout(onDismiss, 4000); // Auto-dismiss after 4s
        return () => clearTimeout(timer);
    }, [onDismiss]);

    // Determine colors and icons based on status
    let color = 'text-emerald-400';
    let bgColor = 'bg-emerald-500/20';
    let borderColor = 'border-emerald-500/50';
    let icon = <CheckCircle size={48} />;
    let statusText = 'SAFE';

    if (status.includes('DANGER') || status.includes('SOS')) {
        color = 'text-red-500';
        bgColor = 'bg-red-500/20';
        borderColor = 'border-red-500/50';
        icon = <AlertTriangle size={48} />;
        statusText = 'DANGER';
    } else if (status.includes('WARNING')) {
        color = 'text-amber-500';
        bgColor = 'bg-amber-500/20';
        borderColor = 'border-amber-500/50';
        icon = <Activity size={48} />;
        statusText = 'WARNING';
    } else if (status.includes('OK') || status.includes('SAFE')) {
        // Defaults
    }

    return (
        <div className="fixed inset-0 z-[6000] flex items-center justify-center pointer-events-none p-6">
            <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -20 }}
                transition={{ type: "spring", damping: 20, stiffness: 300 }}
                className="relative w-full max-w-sm"
            >
                {/* Glass Card */}
                <div className={`relative overflow-hidden rounded-3xl backdrop-blur-xl border-t border-l border-white/20 shadow-2xl ${borderColor} border-b-4 bg-[#020817]/90`}>

                    {/* Background Glow */}
                    <div className={`absolute top-0 right-0 w-64 h-64 ${bgColor} rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2 opacity-50`}></div>

                    <div className="relative p-6 flex flex-col items-center text-center">

                        {/* Status Icon Animation */}
                        <div className="relative mb-6">
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.1, type: "spring" }}
                                className={`w-24 h-24 rounded-full ${bgColor} flex items-center justify-center ${color} border-4 ${borderColor} shadow-[0_0_30px_currentColor]`}
                            >
                                {icon}
                            </motion.div>
                            <motion.div
                                animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                                transition={{ repeat: Infinity, duration: 2 }}
                                className={`absolute inset-0 rounded-full border-2 ${borderColor} opacity-50`}
                            />
                        </div>

                        {/* Title */}
                        <motion.h2
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="text-white text-lg font-medium mb-1 tracking-wide"
                        >
                            STATUS UPDATE
                        </motion.h2>

                        {/* Sender Info */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="flex items-center gap-3 bg-white/5 rounded-full px-4 py-2 border border-white/10 mb-4"
                        >
                            <img src={senderAvatar} className="w-8 h-8 rounded-full bg-black object-cover border border-white/20" />
                            <span className="text-white font-bold tracking-tight">{senderName}</span>
                        </motion.div>

                        {/* Status Text Big */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.5 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.4, type: "spring" }}
                            className={`text-4xl font-black ${color} tracking-tighter drop-shadow-[0_0_10px_rgba(0,0,0,0.8)]`}
                        >
                            {statusText}
                        </motion.div>

                    </div>

                    {/* Footer / Dismiss hint */}
                    <div className="bg-black/40 py-2 text-center text-[10px] text-white/30 font-mono uppercase tracking-widest">
                        Incoming Transmission
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default PulseFeedback;
